import { Copy, KeyRound, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, Tip } from "@/components/ui/overlays";
import { Input } from "@/components/ui/primitives";
import { api } from "@/lib/api";

interface TokenOut {
  id: string;
  name: string;
  created_at: string | null;
  last_used_at: string | null;
}
interface TokenCreated extends TokenOut {
  token: string;
}

/** Mint / copy / revoke personal access tokens for headless clients (the MCP
 * server, CLI, scripts). The secret is shown exactly once, at creation. */
export function TokensDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [tokens, setTokens] = useState<TokenOut[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null); // the just-minted secret

  const refresh = async () => {
    try {
      setTokens(await api.request<TokenOut[]>("GET", "/api/tokens"));
    } catch {
      /* not signed in / unavailable — leave the list empty */
    }
  };

  useEffect(() => {
    if (open) {
      setFresh(null);
      setName("");
      void refresh();
    }
  }, [open]);

  const mint = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const created = await api.request<TokenCreated>("POST", "/api/tokens", {
        name: name.trim(),
      });
      setFresh(created.token);
      setName("");
      await refresh();
    } catch (e) {
      toast.error("Couldn't create token", { description: String(e) });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await api.request("DELETE", `/api/tokens/${id}`);
      await refresh();
      toast("Token revoked");
    } catch (e) {
      toast.error("Couldn't revoke token", { description: String(e) });
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the token and copy it manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Access tokens (MCP / CLI)" className="w-[min(620px,92vw)]">
        <div className="space-y-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            A personal access token lets a headless client — the MCP server, the CLI, a
            script — act as you. Send it as{" "}
            <span className="font-mono text-foreground">Authorization: Bearer &lt;token&gt;</span>.
            The secret is shown <span className="text-foreground">once</span>; store it now.
          </p>

          {fresh && (
            <div className="rounded-lg border border-primary/40 bg-background p-3">
              <p className="mb-2 font-mono text-2xs uppercase tracking-wide text-primary">
                New token — copy it now, it won&apos;t be shown again
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-black/30 px-2 py-1.5 font-mono text-xs text-foreground">
                  {fresh}
                </code>
                <Tip label="Copy">
                  <Button size="icon" variant="ghost" onClick={() => void copy(fresh)} aria-label="Copy">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </Tip>
              </div>
            </div>
          )}

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void mint();
            }}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Token name (e.g. Claude Desktop)"
              aria-label="Token name"
              maxLength={100}
            />
            <Button type="submit" variant="default" disabled={!name.trim() || busy}>
              <KeyRound className="h-3.5 w-3.5" />
              {busy ? "Creating…" : "Create"}
            </Button>
          </form>

          <ul className="space-y-1">
            {tokens.map((t) => (
              <li
                key={t.id}
                className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5"
              >
                <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-[12.5px] text-foreground">{t.name}</span>
                <span className="ml-auto font-mono text-2xs text-muted-foreground">
                  {t.last_used_at
                    ? `used ${new Date(t.last_used_at).toLocaleDateString()}`
                    : "never used"}
                </span>
                <Tip label="Revoke">
                  <button
                    className="text-muted-foreground transition-colors hover:text-destructive"
                    onClick={() => void revoke(t.id)}
                    aria-label="Revoke"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </Tip>
              </li>
            ))}
            {tokens.length === 0 && (
              <li className="px-1 py-2 text-2xs text-muted-foreground">No active tokens.</li>
            )}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
